// chat/actions.ts — the chat control executor for Oriora Habits (cloned from Oriora Expense's
// src/chat/actions.ts, ADAPTED to THIS app's store). The client router (chat/brain.ts) maps ONE
// user message → ONE ChatAction; this file EXECUTES that action against the on-device store
// (src/store.ts). Everything here is LOCAL SQLite over Tauri IPC — no network, no billing — EXCEPT
// the `recap` action, which reuses the app's ONE EXISTING live endpoint (analyzeHabits → /analyze),
// exactly like the Insights panel already does. No new/edited server routes are introduced.
//
// The ChatAction set is derived from what THIS store actually supports:
//   add_habit    → store.createHabit
//   check_in     → store.logEntry (mark done for today)
//   edit_habit   → store.updateHabit  (rename / change cadence / recolour — the store HAS an update cmd)
//   delete_habit → store.deleteHabit  (destructive → HITL-confirmed in the UI)
//   filter       → store.listHabits + local applyHabitFilter (no server query endpoint exists)
//   streak       → local streak math (computeStreak / computeBestStreak) — free, offline
//   recap        → analyzeHabits (the EXISTING /analyze endpoint) — the one billed call
//
// HITL discipline (mirrors Expense): delete_habit is NEVER executed from a chat turn on its own —
// the Chat UI holds it and requires an explicit Confirm tap first. This executor just performs the
// delete once the UI hands it back.

import {
  store, computeStreak, computeBestStreak, todayISO,
  type Cadence, type Habit, type HabitPatch,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "solo-habit-tracker";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { sayWhen } from "./when";
import { analyzeHabits, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact habit row rendered inline in a chat bubble (filter results).
export interface HabitRow {
  id: string;
  name: string;
  cadence: string;
  color: string;
  checkins_30d: number;
  last_logged_on: string | null;
}

// Local, structural filter over the habit list (there is no server query endpoint for this app).
export interface HabitFilter {
  text?: string;
  cadence?: Cadence;
  state?: "active" | "dormant";
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_habit"; name: string; cadence_target: Cadence; color?: string; polarity?: string; schedule?: string }
  | { kind: "check_in"; id: string; label?: string; note?: string | null; on?: string; polarity?: string }
  | { kind: "edit_habit"; id: string; patch: HabitPatch; label?: string }
  | { kind: "delete_habit"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; filter: HabitFilter; label?: string }
  | { kind: "streak"; id?: string; label?: string }
  | { kind: "recap" }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the habit list changed → the main list should refresh
  rows?: HabitRow[];  // filter matches to render inline
}

const DEFAULT_COLOR = "#D6336C"; // the app's brand rose (PALETTE[0] in App.tsx)

const prettyCadence = (c: string) =>
  c === "daily" ? "Daily" : c === "weekly" ? "Weekly" : c === "adhoc" ? "As needed" : c;

// UTC-anchored day diff (mirrors store.ts's internal date math) — avoids DST drift.
/** How a stored schedule reads back to a person. Falls back to the cadence when unset. */
export function prettySchedule(schedule: string | undefined, cadence: string): string {
  const s = (schedule || "").trim();
  if (s.startsWith("days:")) {
    const NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const picked = s.slice(5).split(",").map((n) => NAMES[Number(n.trim())]).filter(Boolean);
    if (picked.length) return picked.join("/");
  }
  if (s.startsWith("count:")) {
    const n = Number(s.slice(6));
    if (n >= 1) return `${n}× a week`;
  }
  if (s === "adhoc") return "whenever";
  return prettyCadence(cadence as Cadence);
}

function daysSince(iso: string | null, today = todayISO()): number {
  if (!iso) return Infinity;
  const p = (s: string) => { const [y, m, d] = s.slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d); };
  return Math.round((p(today) - p(iso)) / 86_400_000);
}

const toRow = (h: Habit): HabitRow => ({
  id: h.id, name: h.name, cadence: h.cadence_target, color: h.color,
  checkins_30d: h.entries_30d, last_logged_on: h.last_logged_on,
});

// "active" = checked in within the last 7 days; "dormant" = never, or >7 days ago.
export function applyHabitFilter(habits: Habit[], f: HabitFilter): Habit[] {
  return habits.filter((h) => {
    if (f.text && !h.name.toLowerCase().includes(f.text.toLowerCase())) return false;
    if (f.cadence && h.cadence_target !== f.cadence) return false;
    if (f.state === "active" && daysSince(h.last_logged_on) > 7) return false;
    if (f.state === "dormant" && daysSince(h.last_logged_on) <= 7) return false;
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_habit": {
        const color = a.color && /^#[0-9a-fA-F]{6}$/.test(a.color) ? a.color : DEFAULT_COLOR;
        const h = await store.createHabit({
          name: a.name, cadence_target: a.cadence_target, color,
          polarity: a.polarity, schedule: a.schedule,
        });
        // The confirmation has to state which KIND was created. "Added Late-night scrolling ·
        // Daily" reads identically whether we understood them or not, and the difference decides
        // what every mark on that row will mean from now on.
        if (h.polarity === "break") {
          return {
            ok: true,
            message: `Added "${h.name}" as something to stop. Mark a day only when you slip — your streak counts the days you stay clean.`,
            changed: true,
          };
        }
        return {
          ok: true,
          // Report the SCHEDULE, not the cadence field. "Added gym · Daily" after being told
          // "mon wed fri" claims we ignored them, when in fact we stored days:1,3,5.
          message: `Added "${h.name}" · ${prettySchedule(h.schedule, h.cadence_target)}. Check in any time — just say "check in ${h.name}".`,
          changed: true,
        };
      }

      case "check_in": {
        const today = todayISO();
        const on = a.on ?? today;
        await store.logEntry(a.id, on, a.note ?? null);
        const when = sayWhen(on, today);
        // A mark on a habit they are BREAKING is a slip, not an achievement — so it is neither
        // congratulated nor decorated with a streak. Saying "3-day streak 🔥" to someone who just
        // told you they smoked would be the app celebrating the thing they came here to stop.
        if (a.polarity === "break") {
          return { ok: true, message: `Recorded a slip on ${a.label ?? "your habit"} for ${when}.`, changed: true };
        }
        const streak = computeStreak(await store.loggedDates(a.id));
        const tail = streak > 0 ? ` ${streak}-day streak${streak >= 7 ? " 🔥" : ""}.` : "";
        return { ok: true, message: `Checked in ${a.label ?? "your habit"} for ${when}.${tail}`, changed: true };
      }

      case "edit_habit":
        await store.updateHabit(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the habit"}.`, changed: true };

      case "delete_habit":
        await store.deleteHabit(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the habit"} and its check-ins.`, changed: true };

      case "filter": {
        const all = await store.listHabits();
        const rows = applyHabitFilter(all, a.filter);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${rows.length} habit${rows.length !== 1 ? "s" : ""}${forLabel}.` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rows.slice(0, 50).map(toRow),
        };
      }

      case "streak": {
        // One named habit → its current + best streak (local math, free).
        if (a.id) {
          const dates = await store.loggedDates(a.id);
          const cur = computeStreak(dates);
          const best = computeBestStreak(dates);
          const name = a.label ?? "This habit";
          if (cur === 0 && best === 0) return { ok: true, message: `${name}: no check-ins yet — log one today to start a streak.` };
          const bestTail = best > cur ? ` (best ${best}).` : ".";
          return { ok: true, message: `${name}: ${cur}-day streak${bestTail}` };
        }
        // Portfolio summary — computed on-device from each habit's dates. Free, offline.
        const habits = await store.listHabits();
        if (habits.length === 0) return { ok: true, message: 'No habits yet — add one with "add habit …".' };
        const withStreaks = await Promise.all(
          habits.map(async (h) => ({ h, streak: computeStreak(await store.loggedDates(h.id)) })),
        );
        withStreaks.sort((x, y) => y.streak - x.streak);
        const live = withStreaks.filter((w) => w.streak > 0);
        const head = `${habits.length} habit${habits.length !== 1 ? "s" : ""} · ${live.length} with a live streak.`;
        const top = live.slice(0, 3).map((w) => `${w.h.name} ${w.streak}d`).join(" · ");
        return { ok: true, message: top ? `${head}\nTop: ${top}` : head };
      }

      case "recap": {
        // The ONE AI feature — reuses the EXISTING live endpoint (analyzeHabits → /analyze),
        // exactly like the Insights panel. Only the derived-signal payload is sent; raw dates stay
        // local. Billed on the Oriora wallet; NO new server route.
        const signals = await store.signalsForAnalysis(todayISO());
        const { analysis } = await analyzeHabits(signals);
        const lines: string[] = [];
        if (analysis.headline) lines.push(analysis.headline);
        const obs = (analysis.observations ?? []).filter((o) => o && o.trim());
        if (obs.length) lines.push(...obs.map((o) => `• ${o}`));
        if (!lines.length && analysis.raw) lines.push(analysis.raw);
        if (!lines.length && analysis.streak_summary) lines.push(analysis.streak_summary);
        if (!lines.length && analysis.one_insight) lines.push(analysis.one_insight);
        return {
          ok: true,
          message: lines.length
            ? lines.join("\n")
            : "Not enough of a pattern to call out yet — keep checking in for a few more days and try again.",
        };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my habits" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole collection). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    // BrainError already carries a friendly, actionable message (funds/auth/offline/no-data).
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
